/** Décalage au sol, en pixels vers l'est et le nord. */
export type GroundOffsetPx = [number, number];

export interface TextFrameBox {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * Boîte englobante de `points` dans le repère d'un texte qui se lit à `frameDeg` (sens horaire depuis
 * l'est) : x dans le sens de lecture, y vers le bas des lettres.
 */
export function textFrameBox(points: GroundOffsetPx[], frameDeg: number): TextFrameBox {
  const rad = (frameDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const box = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
  for (const [east, north] of points) {
    const x = east * cos - north * sin;
    const y = -east * sin - north * cos;
    box.minX = Math.min(box.minX, x);
    box.maxX = Math.max(box.maxX, x);
    box.minY = Math.min(box.minY, y);
    box.maxY = Math.max(box.maxY, y);
  }
  return box;
}

export function textFrameToGround(x: number, y: number, frameDeg: number): GroundOffsetPx {
  const rad = (frameDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return [x * cos - y * sin, -x * sin - y * cos];
}

/**
 * Position du milieu du texte sur un axe de la boîte, l'origine étant le centre de la forme : `align`
 * 0 colle le texte au bord bas de l'axe, 1 au bord haut, 0.5 le laisse au centre de la forme — qui
 * n'est pas forcément le milieu de la boîte.
 */
export function alignedPosition(min: number, max: number, inset: number, textSize: number, align: number): number {
  const low = min + inset + textSize / 2;
  const high = max - inset - textSize / 2;
  if (low > high) return (min + max) / 2;

  const center = Math.min(Math.max(0, low), high);
  return align <= 0.5 ? low + (center - low) * align * 2 : center + (high - center) * (align - 0.5) * 2;
}
